import React, { useMemo } from 'react';
import {
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Box,
  IconButton,
  IconButtonProps,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { ZSpinner, OutlinedIcons } from '@marageti/z4-lib';
import { SalesRecording } from '@marageti/z4-sdk/lib/travel';
import { SalesRecordingType, SalesRecordingStatus } from '@marageti/z4-sdk/lib/travel/SalesRecording';
import { Link } from 'react-router-dom';
import { formatDateSold, salesRecordingTypeLabel } from '../../../utils/sales';

const { OutlinedDocumentDuplicateIcon } = OutlinedIcons;

type CopyProps = {
  textToCopy: string,
} & IconButtonProps;
// Added to support event handling that the CopyButton from the lib does not support
const CopyButton = ({
  textToCopy,
  ...iconButtonProps
}: CopyProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const onClick = async (event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    try {
      await navigator.clipboard.writeText(textToCopy);
      enqueueSnackbar('Copied!', { variant: 'success' });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Unable to copy text to clipboard', e);
    }
  };

  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <IconButton onClick={onClick} sx={{ fontSize: '1.1rem' }} {...iconButtonProps}>
      <OutlinedDocumentDuplicateIcon color="primary" fontSize="inherit" />
    </IconButton>
  );
};

type Props = {
  sales: SalesRecording[],
  hasMore: boolean;
  sentryRef: (e: HTMLDivElement) => void;
};

type Row = {
  id: string;
  dateSold: string;
  referenceId: string;
  traveler: string;
  partner: string;
  type: string;
  totalPrice: string;
  commission: string;
  salesRecordingDetailUrl: string | null;
  status: SalesRecordingStatus | null;
};

const AllSalesTable = ({
  sales,
  sentryRef,
  hasMore,
}: Props) => {
  const rows: Row[] = useMemo(() => (sales.map((sale) => {
    const getSalesRecordingDetails = () => {
      switch (sale.salesRecordingType) {
        case SalesRecordingType.Adjustment:
        case SalesRecordingType.General:
        case SalesRecordingType.CommissionAfterTravel:
        case SalesRecordingType.PlanningFee:
          return `/trip-matches/${sale.tripMatchId}/sales/${sale.id}`;
        case SalesRecordingType.PartnerRepeat:
        case SalesRecordingType.TravelerReferral:
          return `/sales/repeat-referral/${sale.id}`;
        default:
          return null;
      }
    };

    const firstTraveler = sale.travelers?.[0];
    const salesRecordingDetailUrl = getSalesRecordingDetails();

    return {
      id: sale.id,
      dateSold: formatDateSold(sale.createdAt),
      referenceId: sale.referenceId,
      traveler: firstTraveler && firstTraveler.fullName ? firstTraveler.fullName : '-',
      partner: sale.partnerName || sale.partnerId,
      type: salesRecordingTypeLabel[sale.salesRecordingType],
      totalPrice: new Intl.NumberFormat('en-US', { style: 'currency', currency: sale.price.currencyCode }).format(sale.price.amount),
      commission: new Intl.NumberFormat('en-US', { style: 'currency', currency: sale.commission.currencyCode }).format(sale.commission.amount),
      salesRecordingDetailUrl,
      status: sale.status,
    };
  })), [sales]);

  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Recorded</TableCell>
            <TableCell>Trip ID</TableCell>
            <TableCell>Traveler</TableCell>
            <TableCell>Partner</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>Total Price</TableCell>
            <TableCell>Commission</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow
              component={Link}
              to={row.salesRecordingDetailUrl || '#'}
              hover
              key={row.id}
              sx={{
                cursor: 'pointer',
                textDecoration: 'none',
              }}
            >
              <TableCell sx={{ minWidth: '130px' }}>{row.dateSold}</TableCell>
              <TableCell sx={{ minWidth: { desktop: '130px', mobile: '180px' } }}>
                {
                    row.referenceId ? (
                      <Box component="span" display="flex" alignItems="center">
                        {row.referenceId}
                        <CopyButton textToCopy={row.referenceId} size="small" />
                      </Box>
                    ) : '-'
                  }
              </TableCell>
              <TableCell>{row.traveler}</TableCell>
              <TableCell>{row.partner}</TableCell>
              <TableCell sx={{ minWidth: '130px' }}>{row.type}</TableCell>
              <TableCell sx={{ color: row.totalPrice.startsWith('-') ? 'error.main' : undefined }}>
                {row.totalPrice}
              </TableCell>
              <TableCell sx={{ color: row.commission.startsWith('-') ? 'error.main' : undefined }}>
                {row.commission}
              </TableCell>
              <TableCell>{row.status === SalesRecordingStatus.InvoicedStatus && <span style={{ fontStyle: 'italic' }}>Invoiced</span>}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Box display="flex" ref={sentryRef}>
        {hasMore && <ZSpinner />}
      </Box>
    </TableContainer>
  );
};

export default AllSalesTable;
